const PERMISSION_FIELDS = {
  canViewInlets: "viewInletInfo",
  canViewOutlets: "viewAllOutletsAllowed",
  canViewEvents: "viewEventInfo",
  canViewSensors: "viewSensorInfo",
  canViewActivityLog: "viewActivityLog",
  canEditDeviceInfo: "editDeviceInfo",
  canEditOutlets: "editAllOutletsAllowed",
  canSwitchOutlets: "switchAllOutletsAllowed",
  canEditEvents: "editEventInfo",
};

const PERMISSION_KEYS = Object.keys(PERMISSION_FIELDS);

const pendingPermissions = () => ({
  ready: false,
  canViewInlets: false,
  canViewOutlets: false,
  canViewEvents: false,
  canViewSensors: false,
  canViewActivityLog: false,
  canEditDeviceInfo: false,
  canEditOutlets: false,
  canSwitchOutlets: false,
  canEditEvents: false,
  canViewFirmware: false,
});

const unrestrictedReadPermissions = () => ({
  ready: true,
  canViewInlets: true,
  canViewOutlets: true,
  canViewEvents: true,
  canViewSensors: true,
  canViewActivityLog: true,
  canEditDeviceInfo: true,
  canEditOutlets: true,
  canSwitchOutlets: true,
  canEditEvents: true,
  canViewFirmware: false,
});

const normalizeSerial = (value) =>
  value === undefined || value === null ? null : String(value);

const extractGroupItemId = (item) => {
  if (item === undefined || item === null) return null;
  if (typeof item === "string" || typeof item === "number") {
    return String(item);
  }

  return normalizeSerial(
    item.item_id ?? item.id ?? item.enclosureSerialNumber ?? item.serial,
  );
};

const groupContainsDevice = (group, enclosureSerialNumber) => {
  const serial = normalizeSerial(enclosureSerialNumber);
  if (!serial) return false;

  const items = Array.isArray(group?.items) ? group.items : [];
  return items.some((item) => extractGroupItemId(item) === serial);
};

const mergeRoleGrants = (roles = []) =>
  PERMISSION_KEYS.reduce((permissions, key) => {
    const field = PERMISSION_FIELDS[key];
    permissions[key] = roles.some((role) => role?.[field] === true);
    return permissions;
  }, {});

export const getEffectiveDevicePermissions = ({
  user = {},
  resources = {},
  enclosureSerialNumber,
}) => {
  const serial = normalizeSerial(enclosureSerialNumber);
  if (!serial) {
    return pendingPermissions();
  }

  const isAdmin = Boolean(
    user?.is_enterprise_admin || user?.has_admin_privileges,
  );
  if (isAdmin) {
    return {
      ready: true,
      canViewInlets: true,
      canViewOutlets: true,
      canViewEvents: true,
      canViewSensors: true,
      canViewActivityLog: true,
      canEditDeviceInfo: true,
      canEditOutlets: true,
      canSwitchOutlets: true,
      canEditEvents: true,
      canViewFirmware: true,
    };
  }

  if (!resources?.groupsLoaded) {
    return pendingPermissions();
  }

  const relevantGroupRoles = (Array.isArray(resources?.groups) ? resources.groups : [])
    .filter((group) => groupContainsDevice(group, serial))
    .map((group) => group?.role)
    .filter(Boolean);

  const applicableRoles = [user?.role, ...relevantGroupRoles].filter(Boolean);
  if (applicableRoles.length === 0) {
    return unrestrictedReadPermissions();
  }

  return {
    ready: true,
    ...mergeRoleGrants(applicableRoles),
    canViewFirmware: false,
  };
};

export const getEffectiveGroupPermissions = ({
  user = {},
  resources = {},
  group,
}) => {
  const isAdmin = Boolean(
    user?.is_enterprise_admin || user?.has_admin_privileges,
  );
  if (isAdmin) {
    return {
      ready: true,
      canViewInlets: true,
      canViewOutlets: true,
      canViewEvents: true,
      canViewSensors: true,
      canViewActivityLog: true,
      canEditDeviceInfo: true,
      canEditOutlets: true,
      canSwitchOutlets: true,
      canEditEvents: true,
      canViewFirmware: true,
    };
  }

  const applicableRoles = [user?.role, group?.role].filter(Boolean);
  if (applicableRoles.length > 0) {
    return {
      ready: true,
      ...mergeRoleGrants(applicableRoles),
      canViewFirmware: false,
    };
  }

  if (!group && !resources?.groupsLoaded) {
    return pendingPermissions();
  }

  return unrestrictedReadPermissions();
};

export const __test__ = {
  groupContainsDevice,
  mergeRoleGrants,
  normalizeSerial,
};
